/**
 * OIDC sign-in for a public browser client: authorisation code + PKCE (RFC 6749, RFC 7636).
 *
 * Tokens live in memory only (design 11), so nothing readable by other scripts on the origin holds
 * a credential. A reload or a pasted URL loses them; the console then signs in again silently
 * (`prompt=none`) using the issuer's own session, and comes back to the same URL. All that is kept
 * between visits is a marker that this browser has signed in before, holding the email as a login
 * hint; signing out on purpose removes it. Only the PKCE verifier and state cross the redirect, in
 * sessionStorage, and are removed as soon as the code is exchanged.
 */
import { cfg } from "./config";

interface Discovery {
  authorization_endpoint: string;
  token_endpoint: string;
  end_session_endpoint?: string;
}

interface Tokens {
  accessToken: string;
  refreshToken: string | undefined;
  /** epoch ms */
  expiresAt: number;
}

export interface Identity {
  iss: string;
  sub: string;
  email: string | undefined;
  name: string | undefined;
}

let tokens: Tokens | undefined;
let discovery: Discovery | undefined;
const listeners = new Set<() => void>();
const PENDING = "workcore.pkce";
/** localStorage: this browser has signed in before; value is the login hint (email), never a token */
const HINT = "workcore.signedIn";
/** sessionStorage: when this tab last tried a silent sign-in, so a failure cannot loop */
const SILENT_AT = "workcore.silentAt";
/** errors meaning "the issuer needs the user", after which the sign-in page is shown */
const NEEDS_USER = new Set(["login_required", "interaction_required", "consent_required", "account_selection_required"]);

function store(kind: "local" | "session"): Storage | undefined {
  try {
    return kind === "local" ? window.localStorage : window.sessionStorage;
  } catch {
    return undefined; // storage can be blocked; then there is simply no silent sign-in
  }
}

/** The issuer turned a silent sign-in down; the caller shows the sign-in page for `returnTo`. */
export class SilentSignInRefused extends Error {
  constructor(readonly returnTo: string) {
    super("silent sign-in refused");
  }
}

function notify(): void {
  for (const l of listeners) l();
}

export function onAuthChange(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

async function discover(): Promise<Discovery> {
  if (discovery) return discovery;
  const res = await fetch(`${cfg().issuer.replace(/\/$/, "")}/.well-known/openid-configuration`);
  if (!res.ok) throw new Error(`OIDC discovery failed: HTTP ${res.status}`);
  discovery = (await res.json()) as Discovery;
  return discovery;
}

function b64url(bytes: Uint8Array): string {
  let s = "";
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function random(n = 32): string {
  return b64url(crypto.getRandomValues(new Uint8Array(n)));
}

function redirectUri(): string {
  return `${window.location.origin}/callback`;
}

function here(): string {
  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
}

export async function signIn(returnTo = here(), opts: { silent?: boolean } = {}): Promise<void> {
  const d = await discover();
  const verifier = random();
  const state = random(16);
  const challenge = b64url(new Uint8Array(await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier))));
  const silent = opts.silent === true;
  sessionStorage.setItem(PENDING, JSON.stringify({ verifier, state, returnTo, silent }));
  const url = new URL(d.authorization_endpoint);
  const params: Record<string, string> = {
    response_type: "code",
    client_id: cfg().clientId,
    redirect_uri: redirectUri(),
    scope: cfg().scope,
    code_challenge: challenge,
    code_challenge_method: "S256",
    state,
    ...(cfg().authParams ?? {}),
  };
  if (silent) {
    params.prompt = "none";
    const hint = store("local")?.getItem(HINT);
    if (hint && hint !== "1") params.login_hint = hint;
  }
  for (const [k, v] of Object.entries(params)) url.searchParams.set(k, v);
  window.location.assign(url.toString());
}

async function tokenRequest(body: Record<string, string>): Promise<void> {
  const d = await discover();
  const res = await fetch(d.token_endpoint, {
    method: "POST",
    headers: { "content-type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams({ client_id: cfg().clientId, ...body }),
  });
  const t = (await res.json()) as { access_token?: string; refresh_token?: string; expires_in?: number; error?: string; error_description?: string };
  if (!res.ok || !t.access_token) throw new Error(`sign-in failed: ${t.error_description ?? t.error ?? res.status}`);
  tokens = { accessToken: t.access_token, refreshToken: t.refresh_token ?? tokens?.refreshToken, expiresAt: Date.now() + (t.expires_in ?? 300) * 1000 };
  notify();
}

/** Finish the redirect: check state, exchange the code. Returns where to go next. */
export async function completeSignIn(search: string): Promise<string> {
  const q = new URLSearchParams(search);
  const raw = sessionStorage.getItem(PENDING);
  sessionStorage.removeItem(PENDING);
  const pending = raw ? (JSON.parse(raw) as { verifier: string; state: string; returnTo: string; silent?: boolean }) : undefined;
  const error = q.get("error");
  if (error && pending?.silent && NEEDS_USER.has(error)) throw new SilentSignInRefused(pending.returnTo);
  if (error) throw new Error(`sign-in refused: ${q.get("error_description") ?? error}`);
  if (!pending) throw new Error("no sign-in in progress (was this tab reloaded?)");
  if (q.get("state") !== pending.state) throw new Error("sign-in state did not match; start again");
  const code = q.get("code");
  if (!code) throw new Error("no authorisation code returned");
  await tokenRequest({ grant_type: "authorization_code", code, code_verifier: pending.verifier, redirect_uri: redirectUri() });
  store("local")?.setItem(HINT, identity()?.email ?? "1");
  store("session")?.removeItem(SILENT_AT);
  return pending.returnTo && pending.returnTo !== "/callback" ? pending.returnTo : "/";
}

/** A valid access token, refreshing it first if it is about to expire. */
export async function accessToken(): Promise<string | undefined> {
  if (!tokens) return undefined;
  if (tokens.expiresAt - Date.now() < 60_000 && tokens.refreshToken) {
    try {
      await tokenRequest({ grant_type: "refresh_token", refresh_token: tokens.refreshToken });
    } catch {
      signOut();
      return undefined;
    }
  }
  return tokens.accessToken;
}

export function isSignedIn(): boolean {
  return tokens !== undefined;
}

/** Claims from the access token, for display only (the API verifies it). */
export function identity(): Identity | undefined {
  if (!tokens) return undefined;
  try {
    const part = tokens.accessToken.split(".")[1] ?? "";
    const c = JSON.parse(atob(part.replace(/-/g, "+").replace(/_/g, "/"))) as Record<string, unknown>;
    return { iss: String(c.iss), sub: String(c.sub), email: typeof c.email === "string" ? c.email : undefined, name: typeof c.name === "string" ? c.name : undefined };
  } catch {
    return undefined;
  }
}

/**
 * Whether to try a silent sign-in now: this browser has signed in before, and this tab has not tried
 * in the last minute (a refused or looping attempt shows the sign-in page instead). Records the attempt.
 */
export function shouldSignInSilently(): boolean {
  if (tokens || !store("local")?.getItem(HINT)) return false;
  const session = store("session");
  const last = Number(session?.getItem(SILENT_AT) ?? 0);
  if (Date.now() - last < 60_000) return false;
  session?.setItem(SILENT_AT, String(Date.now()));
  return true;
}

/** Drop the tokens. `forget` (a sign-out the user asked for) also stops silent sign-in in this browser. */
export function signOut(opts: { forget?: boolean } = {}): void {
  tokens = undefined;
  if (opts.forget) store("local")?.removeItem(HINT);
  notify();
}
